// Web Push subscription handling. The subscription is registered with the
// DAEMON over the E2E channel — the portal stores nothing and the daemon
// pushes directly to the browser's push service. On iOS this only works from
// an installed (home screen) PWA, 16.4+.
import { b64decode } from "./crypto.js";
import type { Connection } from "./relay.js";

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

// enablePush must run from a user gesture (iOS requirement): asks permission,
// subscribes with the daemon's VAPID key, and registers over the E2E channel.
export async function enablePush(conn: Connection): Promise<void> {
  if (!pushSupported()) throw new Error("This browser doesn't support push notifications.");
  if (!conn.vapidKey) throw new Error("Daemon hasn't sent its push key yet — try again in a second.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not allowed.");
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: b64decode(conn.vapidKey).buffer as ArrayBuffer,
  });
  conn.registerPush(sub.toJSON());
}

// syncPush re-registers an existing subscription after (re)connecting, so the
// daemon always has the current endpoint. No-op without prior permission.
export async function syncPush(conn: Connection): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const sub = await currentSubscription();
  if (!sub) return false;
  try {
    conn.registerPush(sub.toJSON());
    return true;
  } catch {
    return false;
  }
}
